import {
  DashboardIcon,
  MarketplaceIcon,
  CreateStrategiesIcon,
  SidebarResearchIcon,
  MenuSettingsIcon,
} from "assets/icons";
import { DASHBOARD_PATH } from "features/Dashboard/routes";
import { STRATEGY_V2_PATH } from "features/Strategy/routes";
import { RESEARCH_PATH } from "features/Research/routes";
import { MARKETPLACE_V2_PATH } from "features/Explore/routes";
import { ACCOUNT_OVERVIEW_V2_PATH } from "features/Account/routes";

// basic configuration for breadcrumbs
const BREADCRUMB_CONFIG = {
  [DASHBOARD_PATH]: {
    label: "Dashboard",
    icon: <DashboardIcon stroke="#A4A7AE" />,
  },
  [STRATEGY_V2_PATH]: {
    label: "Create",
    icon: <CreateStrategiesIcon stroke="#A4A7AE" />,
  },
  [MARKETPLACE_V2_PATH]: {
    label: "Market",
    icon: <MarketplaceIcon stroke="#A4A7AE" />,
  },
  [RESEARCH_PATH]: {
    label: "Research",
    icon: <SidebarResearchIcon stroke="#A4A7AE" />,
  },
  [ACCOUNT_OVERVIEW_V2_PATH]: {
    label: "Settings",
    icon: <MenuSettingsIcon stroke="#A4A7AE" />,
  },
  "/host": { label: "Host" },
};

// helps format segment labels nicely
const formatLabel = (text) => {
  if (!text) return "";
  const formatted = text.replace(/-/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

// determines the breadcrumb sequence based on path
export const resolveNavbarBreadcrumbs = (
  pathname,
  { strategyType, builderType, detailName } = {}
) => {
  const crumbs = [];

  const addCrumb = (label, icon = null, path = null) => {
    crumbs.push({ label, icon, path });
  };

  // 1. handle root and dashboard
  if (pathname === "/" || pathname === DASHBOARD_PATH) {
    const config = BREADCRUMB_CONFIG[DASHBOARD_PATH];
    return { crumbs: [{ ...config, path: DASHBOARD_PATH }] };
  }

  // 2. static configuration match
  if (BREADCRUMB_CONFIG[pathname]) {
    const config = BREADCRUMB_CONFIG[pathname];
    return { crumbs: [{ ...config, path: pathname }] };
  }

  // 3. detail pages patterns

  // private strategy details
  if (
    pathname.includes("/detail") &&
    (pathname.startsWith("/strategy/") || pathname.startsWith("/create/"))
  ) {
    addCrumb(
      "Strategy",
      BREADCRUMB_CONFIG[STRATEGY_V2_PATH].icon,
      STRATEGY_V2_PATH
    );
    addCrumb("Detail");
    if (detailName) addCrumb(detailName);
    return { crumbs };
  }

  // market strategy details
  if (
    pathname.startsWith("/markets/strategy/") &&
    pathname.includes("/detail")
  ) {
    addCrumb(
      "Market",
      BREADCRUMB_CONFIG[MARKETPLACE_V2_PATH].icon,
      MARKETPLACE_V2_PATH
    );
    addCrumb("Strategies");
    if (detailName) addCrumb(detailName);
    return { crumbs };
  }

  // host details
  if (pathname.startsWith("/markets/host/") && pathname.includes("/detail")) {
    addCrumb(
      "Market",
      BREADCRUMB_CONFIG[MARKETPLACE_V2_PATH].icon,
      MARKETPLACE_V2_PATH
    );
    addCrumb("Managers");
    if (detailName) addCrumb(detailName);
    return { crumbs };
  }

  // stock details in research
  if (
    pathname.startsWith("/research/stock/") &&
    pathname.includes("/details")
  ) {
    addCrumb("Research", BREADCRUMB_CONFIG[RESEARCH_PATH].icon, RESEARCH_PATH);
    addCrumb("Stock Details");
    if (detailName) addCrumb(detailName);
    return { crumbs };
  }

  // market collection details
  if (
    pathname.startsWith("/markets/collection/") &&
    pathname.includes("/detail")
  ) {
    addCrumb(
      "Market",
      BREADCRUMB_CONFIG[MARKETPLACE_V2_PATH].icon,
      MARKETPLACE_V2_PATH
    );
    addCrumb("Collection");
    if (detailName) addCrumb(detailName);
    return { crumbs };
  }

  // 4. building specific logic
  if (pathname.startsWith("/strategy") || pathname.startsWith("/create")) {
    const segments = pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    const secondLast = segments[segments.length - 2];

    if (last === "etf" || secondLast === "etf") {
      addCrumb(
        "Create",
        BREADCRUMB_CONFIG[STRATEGY_V2_PATH].icon,
        STRATEGY_V2_PATH
      );
      const type = builderType || strategyType;
      const titles = { etf: "ETF Builder", noCode: "No code builder" };
      addCrumb(titles[type] || "ETF Builder");
      return { crumbs };
    }
    if (last === "edit") {
      addCrumb(
        "Create",
        BREADCRUMB_CONFIG[STRATEGY_V2_PATH].icon,
        STRATEGY_V2_PATH
      );
      addCrumb("Code builder");
      return { crumbs };
    }
    if (pathname.includes("ncb") || pathname.includes("ncb-edit")) {
      addCrumb(
        "Create",
        BREADCRUMB_CONFIG[STRATEGY_V2_PATH].icon,
        STRATEGY_V2_PATH
      );
      addCrumb("No code builder");
      return { crumbs };
    }
  }

  // 5. fallback to segment matching
  const segments = pathname.split("/").filter(Boolean);
  const root = `/${segments[0]}`;
  const config = BREADCRUMB_CONFIG[root];

  if (config) {
    addCrumb(config.label, config.icon, root);
  } else {
    addCrumb(formatLabel(segments[0]));
  }

  // add sub-segments if any
  if (segments.length > 1) {
    segments.slice(1).forEach((seg) => {
      if (seg === "ncb") addCrumb("No code builder");
      else addCrumb(formatLabel(seg));
    });
  }

  return { crumbs };
};
